"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Ban,
  CalendarCheck2,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  History,
  PackageCheck,
  RefreshCcw,
  RotateCcw,
  X
} from "lucide-react";
import type { Customer, CustomerHistorySnapshot } from "./types";
import {
  buildCustomerHistoryPresentation,
  type CustomerHistoryCategory,
  type CustomerHistoryPresentation,
  type CustomerHistoryPresentationEvent
} from "./lib/customerHistory";
import { studioApi, studioErrorCopy } from "./lib/api";

type HistoryFilter = "all" | CustomerHistoryCategory;

const historyFilters: Array<{ id: HistoryFilter; label: string }> = [
  { id: "all", label: "Tüm geçmiş" },
  { id: "package", label: "Paketler" },
  { id: "session", label: "Seanslar" },
  { id: "cancellation", label: "İptaller" },
  { id: "payment", label: "Ödemeler" },
  { id: "correction", label: "Düzeltmeler" }
];

const historyMoney = (amount: number) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0
  }).format(amount);

const historyDate = (value?: string) => {
  if (!value) return "—";
  const date = new Date(value.length === 10 ? `${value}T12:00:00+03:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("tr-TR", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  }).format(date);
};

const historyDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("tr-TR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
};

const historySource = (value?: string) => {
  if (value === "CUSTOMER_APP") return "Mobil uygulama";
  if (value === "STAFF_WEB") return "Personel portalı";
  if (value === "MIGRATION") return "Eski sistem";
  if (value === "SYSTEM") return "Sistem";
  return value;
};

function HistoryEventIcon({ event }: { event: CustomerHistoryPresentationEvent }) {
  if (event.category === "payment") return <CircleDollarSign size={17} />;
  if (event.category === "cancellation") return <Ban size={17} />;
  if (event.category === "correction") return <RotateCcw size={17} />;
  if (event.category === "package") return <PackageCheck size={17} />;
  return event.type === "SESSION_COMPLETED"
    ? <CheckCircle2 size={17} />
    : <CalendarCheck2 size={17} />;
}

export function CustomerHistoryView({
  presentation
}: {
  presentation: CustomerHistoryPresentation;
}) {
  const [filter, setFilter] = useState<HistoryFilter>("all");
  const [expandedPackages, setExpandedPackages] = useState<Set<string>>(
    () => new Set(
      presentation.packages
        .filter((item) => item.expandedByDefault)
        .map((item) => item.id)
    )
  );

  const filteredPackages = useMemo(() => presentation.packages.map((item) => ({
    ...item,
    visibleEvents: item.events.filter((event) => filter === "all" || event.category === filter)
  })), [filter, presentation]);

  function togglePackage(packageId: string) {
    setExpandedPackages((current) => {
      const next = new Set(current);
      if (next.has(packageId)) next.delete(packageId);
      else next.add(packageId);
      return next;
    });
  }

  return (
    <div className="customer-history-view">
      <section className="customer-history-hero">
        <div>
          <span className="eyebrow">Danışan yolculuğu</span>
          <h3>{presentation.customer.name}</h3>
          <p>{historyDate(presentation.customer.registrationDate)} tarihinden bugüne paket, seans ve ödeme akışı.</p>
        </div>
        <div className="customer-history-stats" aria-label="Müşteri geçmişi özeti">
          <div><span>Paket</span><strong>{presentation.stats.packageCount}</strong></div>
          <div><span>Alınan seans</span><strong>{presentation.stats.purchasedSessions}</strong></div>
          <div><span>Kullanılan</span><strong>{presentation.stats.usedSessions}</strong></div>
          <div><span>Tahsilat</span><strong>{historyMoney(presentation.stats.paidAmount)}</strong></div>
          <div className={presentation.stats.outstandingAmount > 0 ? "pending" : ""}>
            <span>Kalan ödeme</span>
            <strong>{historyMoney(presentation.stats.outstandingAmount)}</strong>
          </div>
        </div>
      </section>

      <div className="customer-history-filter" aria-label="Geçmiş filtresi">
        {historyFilters.map((item) => (
          <button
            type="button"
            key={item.id}
            className={filter === item.id ? "active" : ""}
            aria-pressed={filter === item.id}
            onClick={() => setFilter(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="customer-history-packages">
        {filteredPackages.map((item) => {
          const expanded = expandedPackages.has(item.id);
          const active = item.statusCode === "ACTIVE" && item.isOperational;
          const progress = item.quantity > 0
            ? Math.max(0, Math.min(100, ((item.quantity - item.remainingSessions) / item.quantity) * 100))
            : 0;
          return (
            <article className={`customer-history-package ${active ? "active" : ""}`} key={item.id}>
              <button
                type="button"
                className="customer-history-package-head"
                aria-expanded={expanded}
                onClick={() => togglePackage(item.id)}
              >
                <span className="customer-history-package-mark"><PackageCheck size={19} /></span>
                <span className="customer-history-package-title">
                  <span>
                    <strong>{item.title}</strong>
                    <i>{active ? "Aktif paket" : item.statusCode === "CLOSED" ? "Tamamlandı" : "Arşiv"}</i>
                  </span>
                  <small>{historyDate(item.purchasedOn)} · Son kullanım {historyDate(item.expiresOn)}</small>
                </span>
                <span className="customer-history-package-balance">
                  <strong>{item.remainingSessions}<small> / {item.quantity}</small></strong>
                  <span>seans kaldı</span>
                </span>
                <ChevronDown className={expanded ? "open" : ""} size={19} />
              </button>

              <div className="customer-history-package-progress" aria-hidden="true">
                <span style={{ width: `${progress}%` }} />
              </div>

              {expanded && (
                <div className="customer-history-package-body">
                  <div className="customer-history-package-facts">
                    <span><small>Paket tutarı</small><strong>{historyMoney(item.price)}</strong></span>
                    <span><small>Ödenen</small><strong>{historyMoney(item.paidAmount)}</strong></span>
                    <span className={item.remainingAmount > 0 ? "pending" : ""}><small>Kalan</small><strong>{historyMoney(item.remainingAmount)}</strong></span>
                    <span><small>İptal hakkı</small><strong>{item.remainingCancellations}</strong></span>
                  </div>

                  <div className="customer-history-rail" data-history-rail={item.id}>
                    {item.visibleEvents.length ? item.visibleEvents.map((event) => (
                      <div className={`customer-history-event ${event.category}`} key={event.id}>
                        <span className="customer-history-event-dot"><HistoryEventIcon event={event} /></span>
                        <time dateTime={event.occurredAt}>{historyDateTime(event.occurredAt)}</time>
                        <div className="customer-history-event-copy">
                          <strong>{event.title}</strong>
                          {event.detail && <span>{event.detail}</span>}
                          {(event.trainer || event.sourceCode) && (
                            <small>{[event.trainer, historySource(event.sourceCode)].filter(Boolean).join(" · ")}</small>
                          )}
                        </div>
                        {event.balanceLabel && (
                          <span className={`customer-history-balance-change ${event.balanceDetail ? "detailed" : ""}`}>
                            <strong>{event.balanceLabel}</strong>
                            {event.balanceDetail && <small>{event.balanceDetail}</small>}
                          </span>
                        )}
                      </div>
                    )) : (
                      <div className="customer-history-filter-empty">
                        Bu pakette seçilen türde hareket bulunmuyor.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </article>
          );
        })}

        {!filteredPackages.length && (
          <div className="customer-history-empty">
            <History size={25} />
            <strong>Henüz paket geçmişi yok</strong>
            <span>İlk paket oluşturulduğunda kronolojik akış burada başlayacak.</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function CustomerHistorySummary({
  customer,
  onClose
}: {
  customer: Customer;
  onClose: () => void;
}) {
  const [snapshot, setSnapshot] = useState<CustomerHistorySnapshot | null>(null);
  const [error, setError] = useState("");
  const [requestKey, setRequestKey] = useState(0);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    setSnapshot(null);
    setError("");
    studioApi.customerHistory(customer.id, controller.signal)
      .then(setSnapshot)
      .catch((requestError) => {
        if (requestError instanceof DOMException && requestError.name === "AbortError") return;
        setError(studioErrorCopy(requestError, "Müşteri geçmişi şu anda yüklenemedi."));
      });
    return () => controller.abort();
  }, [customer.id, requestKey]);

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    closeButtonRef.current?.focus();
    return () => previousFocus?.focus();
  }, []);

  const presentation = useMemo(
    () => snapshot ? buildCustomerHistoryPresentation(snapshot) : null,
    [snapshot]
  );

  return (
    <div
      className="customer-history-layer"
      role="dialog"
      aria-modal="true"
      aria-labelledby="customer-history-title"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <button className="customer-history-scrim" type="button" onClick={onClose} aria-label="Özeti kapat" />
      <section className="customer-history-modal">
        <header className="customer-history-modal-head">
          <div>
            <span className="eyebrow">The Heal Hub · Müşteri özeti</span>
            <h2 id="customer-history-title">Tüm yolculuk, tek akışta</h2>
          </div>
          <button ref={closeButtonRef} className="icon-button" type="button" onClick={onClose} aria-label="Müşteri özetini kapat">
            <X size={21} />
          </button>
        </header>

        <div className="customer-history-modal-content" aria-live="polite">
          {!presentation && !error && (
            <div className="customer-history-loading">
              <span className="customer-history-loader" />
              <strong>Geçmiş hazırlanıyor</strong>
              <p>{customer.name} için paket, seans, iptal ve ödemeler sıralanıyor.</p>
            </div>
          )}
          {error && (
            <div className="customer-history-error" role="alert">
              <AlertCircle size={26} />
              <strong>Özet yüklenemedi</strong>
              <span>{error}</span>
              <button type="button" className="button secondary" onClick={() => setRequestKey((key) => key + 1)}>
                <RefreshCcw size={16} /> Yeniden dene
              </button>
            </div>
          )}
          {presentation && <CustomerHistoryView presentation={presentation} />}
        </div>
      </section>
    </div>
  );
}
